import React, { useEffect, useRef, useState } from "react";
import useFetch from "hooks/useFetch";
import { useNavigate } from "react-router-dom";
import { RoutePaths } from "enums/RoutePaths";
import Select, { createFilter } from "react-select";
import {
  Alert,
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Pagination,
  SegmentedControl,
  Table,
  TextField,
  styles as ui,
  useConfirm,
} from "stories/Admin";
import { adminSelectStyles } from "./adminSelectStyles";
import RowMenu from "./RowMenu";
import styles from "./UsersPage.module.scss";

interface User {
  uid: string;
  email: string;
  username: string;
  allowedOperations: string | null;
  cached: boolean;
  /** Har et push-token, så superadmin kan sende push til brukeren. */
  hasPushToken?: boolean;
}

interface Mosque {
  orgNr: string;
  orgName: string;
  name?: string;
}

interface MosqueOption {
  value: string;
  label: string;
  orgName: string;
  name?: string;
}

// Backend sender «SUPERADMIN» i stedet for selve nøkkelen.
const superAdminCode = "SUPERADMIN";

const FilterAllowedUsersButton: React.FC<{
  users: User[];
  setFilteredUsers: React.Dispatch<React.SetStateAction<User[]>>;
  searchTerm: string;
  selectedMosqueFilter: string | null;
}> = ({ users, setFilteredUsers, searchTerm, selectedMosqueFilter }) => {
  const [showOnlyAdmins, setShowOnlyAdmins] = useState(false);

  const applyFilters = (userList: User[]) => {
    const lowerSearchTerm = searchTerm.toLowerCase();

    return userList.filter((user) => {
      const isSuperAdmin = user.allowedOperations === superAdminCode;
      const matchesSearchTerm =
        user.email.toLowerCase().includes(lowerSearchTerm) ||
        user.username.toLowerCase().includes(lowerSearchTerm) ||
        user.uid.toLowerCase().includes(lowerSearchTerm) ||
        (user.allowedOperations &&
          user.allowedOperations.toLowerCase().includes(lowerSearchTerm)) ||
        (lowerSearchTerm === "super admin" && isSuperAdmin);

      const matchesMosqueFilter =
        !selectedMosqueFilter ||
        user.allowedOperations === selectedMosqueFilter;

      return matchesSearchTerm && matchesMosqueFilter;
    });
  };

  useEffect(() => {
    setShowOnlyAdmins(false);
  }, [searchTerm, selectedMosqueFilter]);

  const handleToggle = () => {
    if (!showOnlyAdmins) {
      const admins = users.filter((user) => user.allowedOperations !== null);
      setFilteredUsers(applyFilters(admins));
    } else {
      setFilteredUsers(applyFilters(users));
    }
    setShowOnlyAdmins(!showOnlyAdmins);
  };

  return (
    <SegmentedControl
      label="Vis brukere"
      value={showOnlyAdmins ? "admins" : "all"}
      onChange={(v) => {
        if ((v === "admins") !== showOnlyAdmins) handleToggle();
      }}
      options={[
        { value: "all", label: "Alle brukere" },
        { value: "admins", label: "Bare admins" },
      ]}
    />
  );
};

const UsersPage: React.FC = () => {
  const { execute, lastError } = useFetch();
  const createApi = useFetch<User>();
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [createError, setCreateError] = useState("");
  const [notice, setNotice] = useState("");
  const [loadError, setLoadError] = useState("");
  const creating = useRef(false);
  const createButton = useRef<HTMLButtonElement>(null);

  const closeCreate = () => {
    if (creating.current) return;
    setShowCreate(false);
    setNewEmail("");
    setNewPassword("");
    setCreateError("");
    createButton.current?.focus();
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (creating.current) return;
    creating.current = true;
    setCreateError("");
    try {
      const created = await createApi.execute("/api/v1/users/admin", "POST", {
        email: newEmail.trim(),
        password: newPassword,
      });
      if (!created) {
        setCreateError(
          createApi.lastError()?.message ||
            "Kunne ikke opprette brukeren. Prøv igjen.",
        );
        return;
      }
      setUsers((previous) => [
        created,
        ...previous.filter((user) => user.uid !== created.uid),
      ]);
      setSearchTerm(created.email);
      setSelectedMosqueFilter(null);
      setNotice(
        `Brukeren ${created.email} er opprettet og kan logge inn i appen.`,
      );
      creating.current = false;
      closeCreate();
    } catch {
      setCreateError("Kunne ikke opprette brukeren. Prøv igjen.");
    } finally {
      creating.current = false;
    }
  };
  const [users, setUsers] = useState<User[]>([]);
  const [mosques, setMosques] = useState<Mosque[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedMosqueOrgNr, setSelectedMosqueOrgNr] = useState("");
  const [selectedMosqueFilter, setSelectedMosqueFilter] = useState<
    string | null
  >(null);
  // uid → alle moskeene brukeren er admin for (en bruker kan administrere flere).
  const [adminMosques, setAdminMosques] = useState<Record<string, string[]>>(
    {},
  );
  const confirm = useConfirm();
  const navigate = useNavigate();

  const mosquesOf = (user: User): string[] => {
    const list = [...(adminMosques[user.uid] ?? [])];
    if (
      user.allowedOperations &&
      user.allowedOperations !== superAdminCode &&
      user.allowedOperations !== "Super Admin" &&
      user.allowedOperations !== "MAWAQEET" &&
      !list.includes(user.allowedOperations)
    ) {
      list.unshift(user.allowedOperations);
    }
    return list;
  };

  const mosqueOptions: MosqueOption[] = mosques.map((mosque) => ({
    value: mosque.orgNr,
    label: (mosque.name || mosque.orgName)?.toUpperCase(),
    orgName: mosque.orgName,
  }));

  const customFilter = createFilter({
    ignoreCase: true,
    ignoreAccents: true,
    matchFrom: "any",
    stringify: (option) => option.label,
  });

  const fetchUsers = async () => {
    const usersResult = (await execute("/api/v1/users", "GET")) as User[];
    if (usersResult) {
      setUsers(usersResult);
      setLoadError("");
    } else {
      setLoadError(
        lastError()?.message || "Kunne ikke hente brukerlisten. Prøv igjen.",
      );
    }
  };

  const fetchMosques = async () => {
    const mosquesResult = (await execute("/api/v1/mosques", "GET")) as Mosque[];
    if (mosquesResult) {
      const sortedMosques = mosquesResult.sort((a, b) =>
        a.orgName.localeCompare(b.orgName),
      );
      setMosques(sortedMosques);
    }
  };

  const fetchAdminMosques = async () => {
    const links = (await execute(
      "/api/v1/admin/mosque-admins",
      "GET",
    )) as Array<{ uid: string; orgNr: string }> | null;
    if (!links) return;
    const byUid: Record<string, string[]> = {};
    links.forEach((link) => {
      byUid[link.uid] = [...(byUid[link.uid] ?? []), link.orgNr];
    });
    setAdminMosques(byUid);
  };

  useEffect(() => {
    fetchUsers();
    fetchMosques();
    fetchAdminMosques();
  }, []);

  useEffect(() => {
    if (searchTerm.length < 3 && !selectedMosqueFilter) {
      setFilteredUsers(users);
      return;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();

    const filtered = users.filter((user) => {
      const isSuperAdmin = user.allowedOperations === superAdminCode;

      const email = user.email?.toLowerCase() || "";
      const username = user.username?.toLowerCase() || "";
      const uid = user.uid?.toLowerCase() || "";
      const allowedOperations = user.allowedOperations?.toLowerCase() || "";

      const matchesSearchTerm =
        searchTerm.length < 3 ||
        email.includes(lowerSearchTerm) ||
        username.includes(lowerSearchTerm) ||
        uid.includes(lowerSearchTerm) ||
        allowedOperations.includes(lowerSearchTerm) ||
        (lowerSearchTerm === "super admin" && isSuperAdmin);

      const matchesMosqueFilter =
        !selectedMosqueFilter || mosquesOf(user).includes(selectedMosqueFilter);

      return matchesSearchTerm && matchesMosqueFilter;
    });

    setFilteredUsers(filtered.length > 0 ? filtered : []);
  }, [searchTerm, selectedMosqueFilter, users, adminMosques]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredUsers]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleAdminToggleClick = (user: User) => {
    setSelectedUser(user);
    setSelectedMosqueOrgNr("");
    setShowModal(true);
  };

  const handleSelectChange = (newValue: unknown) => {
    const selectedOption = newValue as MosqueOption;
    setSelectedMosqueOrgNr(selectedOption ? selectedOption.value : "");
  };

  const handleSearchChange = (e: any) => {
    setSearchTerm(e.target.value);
    if (e.target.value === "") {
      setFilteredUsers(users);
    }
  };

  const handleRemoveRightsClick = async (user: User) => {
    const ok = await confirm({
      title: "Fjern rettigheter?",
      message: (
        <>
          <b>{user.email}</b> mister admin-tilgangen til alle moskeer.
        </>
      ),
      confirmLabel: "Fjern rettigheter",
      tone: "danger",
    });
    if (ok) await handleRemoveRightsConfirm(user);
  };

  const handleRemoveRightsConfirm = async (selectedUser: User) => {
    if (selectedUser) {
      try {
        await execute(`/api/v1/users/${selectedUser.uid}/delete`, "PATCH");

        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.uid === selectedUser.uid ? { ...u, allowedOperations: null } : u,
          ),
        );
        setAdminMosques((prev) => ({ ...prev, [selectedUser.uid]: [] }));

        setSelectedUser(null);
      } catch (error) {
        console.error("En feil oppstod ved fjerning av rettigheter:", error);
      }
    }
  };

  const handleSuperAdminClick = async (user: User) => {
    const ok = await confirm({
      title: "Gjør til superadmin?",
      message: (
        <>
          <b>{user.email}</b> får tilgang til hele superadmin-panelet.
        </>
      ),
      confirmLabel: "Gjør til superadmin",
    });
    if (ok) await handleSuperAdminConfirm(user);
  };

  const [savingAccess, setSavingAccess] = useState(false);
  // Fersk versjon av brukeren i dialogen (hovedmoskeen kan ha flyttet seg etter en endring).
  const modalUser =
    users.find((u) => u.uid === selectedUser?.uid) ?? selectedUser;

  const refreshAccess = async () => {
    await Promise.all([fetchUsers(), fetchAdminMosques()]);
  };

  const handleAddMosque = async () => {
    if (!selectedUser || !selectedMosqueOrgNr) return;
    setSavingAccess(true);
    await execute(
      `/api/v1/admin/users/${selectedUser.uid}/mosques/${selectedMosqueOrgNr}`,
      "POST",
    );
    setSelectedMosqueOrgNr("");
    await refreshAccess();
    setSavingAccess(false);
  };

  const handleRemoveMosque = async (orgNr: string) => {
    if (!selectedUser) return;
    setSavingAccess(true);
    await execute(
      `/api/v1/admin/users/${selectedUser.uid}/mosques/${orgNr}`,
      "DELETE",
    );
    await refreshAccess();
    setSavingAccess(false);
  };

  const handleSuperAdminConfirm = async (selectedUser: User) => {
    if (selectedUser) {
      await execute(`/api/v1/users/${selectedUser.uid}/superadmin`, "PATCH");

      const updatedUser = {
        ...selectedUser,
        allowedOperations: superAdminCode,
      };

      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.uid === selectedUser.uid ? updatedUser : u)),
      );

      setSelectedUser(null);
    }
  };

  const handleDeleteClick = async (user: User) => {
    const ok = await confirm({
      title: "Slett bruker?",
      message: (
        <>
          Brukeren <b>{user.email}</b> slettes permanent. Dette kan ikke angres.
        </>
      ),
      confirmLabel: "Slett bruker",
      tone: "danger",
    });
    if (ok) await handleDeleteConfirm(user);
  };

  const handleDeleteConfirm = async (selectedUser: User) => {
    if (selectedUser) {
      await execute(`/api/v1/users/${selectedUser.uid}`, "DELETE");

      setUsers((prevUsers) =>
        prevUsers.filter((u) => u.uid !== selectedUser.uid),
      );

      setSelectedUser(null);
    }
  };

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const accessBadge = (user: User) => {
    if (
      user.allowedOperations === superAdminCode ||
      user.allowedOperations === "Super Admin"
    ) {
      return <Badge tone="gold">Superadmin</Badge>;
    }
    const orgNrs = mosquesOf(user);
    if (orgNrs.length > 0) {
      return (
        <span className={styles.rowActions}>
          {orgNrs.map((orgNr) => (
            <Badge key={orgNr} tone="mint">
              {mosques.find((mosque) => mosque.orgNr === orgNr)?.orgName ||
                orgNr}
            </Badge>
          ))}
        </span>
      );
    }
    if (user.allowedOperations) {
      return <Badge tone="mint">{user.allowedOperations}</Badge>;
    }
    return <Badge tone="grey">Ingen tilgang</Badge>;
  };

  const columns: Array<Column<User>> = [
    {
      key: "email",
      header: "E-post",
      render: (user) => (
        <span className={styles.userCell}>
          <span className={styles.avatar} aria-hidden="true">
            {(user.email || user.username || "?").charAt(0).toUpperCase()}
          </span>
          <b className={styles.email}>{user.email}</b>
        </span>
      ),
    },
    {
      key: "username",
      header: "Brukernavn",
      render: (user) => <span className={styles.muted}>{user.username}</span>,
    },
    { key: "access", header: "Tilgang", render: accessBadge },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (user) => (
        <span className={styles.rowActions}>
          <Button
            variant="secondary"
            size="sm"
            icon={<Icon name="key" size={14} />}
            onClick={() => handleAdminToggleClick(user)}
          >
            Gi tilgang
          </Button>
          <RowMenu
            items={[
              {
                label: "Gi moskéadmin-tilgang",
                icon: "mosque",
                onClick: () => handleAdminToggleClick(user),
              },
              ...(user.hasPushToken === false
                ? []
                : [
                    {
                      label: "Send push",
                      icon: "bell" as const,
                      onClick: () =>
                        navigate(
                          `${RoutePaths.DirectPush}?bruker=${encodeURIComponent(user.uid)}`,
                        ),
                    },
                  ]),
              {
                label: "Gjør til superadmin",
                icon: "star",
                onClick: () => handleSuperAdminClick(user),
              },
              {
                label: "Fjern rettigheter",
                icon: "x",
                danger: true,
                onClick: () => handleRemoveRightsClick(user),
              },
              {
                label: "Slett bruker",
                icon: "trash",
                danger: true,
                onClick: () => handleDeleteClick(user),
              },
            ]}
          />
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Brukere og tilganger"
        subtitle="Gi og fjern admin-tilgang for moskeer, eller gjør brukere til superadmin."
        actions={
          <Button
            ref={createButton}
            onClick={() => {
              setNotice("");
              setShowCreate(true);
            }}
          >
            Opprett bruker
          </Button>
        }
      />

      {notice && <Alert tone="ok">{notice}</Alert>}
      {loadError && (
        <Alert tone="error">
          {loadError}{" "}
          <Button variant="secondary" size="sm" onClick={fetchUsers}>
            Prøv igjen
          </Button>
        </Alert>
      )}
      {showCreate && (
        <div
          className={ui.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeCreate();
          }}
        >
          <form
            className={`${ui.modal} ${styles.accessModal}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="opprett-bruker-tittel"
            onSubmit={handleCreate}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                closeCreate();
              }
              if (e.key === "Tab") {
                const fields = Array.from(
                  e.currentTarget.querySelectorAll<HTMLElement>(
                    "input:not(:disabled), button:not(:disabled)",
                  ),
                );
                const first = fields[0],
                  last = fields[fields.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                  e.preventDefault();
                  last?.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                  e.preventDefault();
                  first?.focus();
                }
              }
            }}
          >
            <h2 className={ui.mh} id="opprett-bruker-tittel">
              Opprett bruker
            </h2>
            <div className={ui.mb}>
              <p>
                Brukeren kan logge inn i iBønn med e-postadressen og passordet
                du velger.
              </p>
              <TextField
                label="E-postadresse"
                type="email"
                autoComplete="off"
                autoFocus
                required
                maxLength={254}
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={createApi.isLoading}
              />
              <TextField
                label="Passord"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                maxLength={128}
                help="Minst 8 tegn."
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={createApi.isLoading}
              />
              {createError && <Alert tone="error">{createError}</Alert>}
            </div>
            <div className={ui.mf}>
              <Button
                type="button"
                variant="secondary"
                onClick={closeCreate}
                disabled={createApi.isLoading}
              >
                Avbryt
              </Button>
              <Button type="submit" loading={createApi.isLoading}>
                Opprett bruker
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className={styles.filters}>
        <TextField
          aria-label="Søk"
          type="search"
          placeholder="Søk på e-post, brukernavn, Firebase-ID eller tilgang (min. 3 tegn)"
          className={styles.search}
          fieldClassName={styles.searchField}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <div className={styles.mosqueFilter}>
          <Select
            aria-label="Filtrer på moské"
            options={mosqueOptions}
            onChange={(option) =>
              setSelectedMosqueFilter(
                option ? (option as MosqueOption).value : null,
              )
            }
            placeholder="Filtrer på moské"
            filterOption={customFilter}
            isClearable
            styles={adminSelectStyles<MosqueOption>()}
            noOptionsMessage={() => "Ingen moskeer funnet"}
          />
        </div>
        <FilterAllowedUsersButton
          users={users}
          setFilteredUsers={setFilteredUsers}
          searchTerm={searchTerm}
          selectedMosqueFilter={selectedMosqueFilter}
        />
      </div>

      <Card className={styles.tableCard}>
        <Table
          columns={columns}
          rows={currentItems}
          rowKey={(user) => user.uid}
          empty={
            <EmptyState icon="users" title="Ingen brukere funnet">
              Prøv et annet søk eller filter.
            </EmptyState>
          }
        />
        <Pagination
          page={currentPage}
          pageCount={totalPages}
          onChange={paginate}
        />
      </Card>

      {showModal && (
        <div
          className={ui.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}
        >
          <div
            className={`${ui.modal} ${styles.accessModal}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="gi-tilgang-tittel"
            onKeyDown={(e) => {
              if (e.key === "Escape") setShowModal(false);
            }}
          >
            <h2 className={ui.mh} id="gi-tilgang-tittel">
              <span className={styles.modalIcon} aria-hidden="true">
                <Icon name="key" size={18} />
              </span>
              Moskéadmin-tilganger
              <IconButton
                label="Lukk"
                size="sm"
                className={styles.modalClose}
                icon={<Icon name="x" size={16} />}
                onClick={() => setShowModal(false)}
              />
            </h2>
            <div className={ui.mb}>
              <span>
                Moskeene <b>{selectedUser?.email}</b> er admin for.
              </span>
              {modalUser && mosquesOf(modalUser).length > 0 ? (
                <ul className={styles.accessList}>
                  {mosquesOf(modalUser).map((orgNr) => {
                    const mosque = mosques.find((m) => m.orgNr === orgNr);
                    return (
                      <li key={orgNr} className={styles.accessItem}>
                        <span className={styles.accessName}>
                          <b>{mosque?.name || mosque?.orgName || orgNr}</b>
                          <span className={styles.muted}>{orgNr}</span>
                        </span>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={savingAccess}
                          onClick={() => handleRemoveMosque(orgNr)}
                        >
                          Fjern
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <span className={styles.muted}>Ingen moskeer ennå.</span>
              )}
              <div className={styles.accessAdd}>
                <div className={styles.accessSelect}>
                  <Select
                    aria-label="Legg til moské"
                    options={mosqueOptions.filter(
                      (o) =>
                        !modalUser || !mosquesOf(modalUser).includes(o.value),
                    )}
                    value={
                      mosqueOptions.find(
                        (o) => o.value === selectedMosqueOrgNr,
                      ) ?? null
                    }
                    onChange={handleSelectChange}
                    placeholder="Søk og legg til moské"
                    filterOption={customFilter}
                    styles={adminSelectStyles<MosqueOption>()}
                    menuPortalTarget={document.body}
                    noOptionsMessage={() => "Ingen moskeer funnet"}
                  />
                </div>
                <Button
                  icon={<Icon name="plus" size={16} />}
                  onClick={handleAddMosque}
                  disabled={!selectedMosqueOrgNr}
                  loading={savingAccess}
                >
                  Legg til
                </Button>
              </div>
              <Alert tone="info">
                Brukeren får tilgang til alle moské-sidene for hver moské. Med
                flere moskeer kan de bytte mellom dem i admin-panelet og i
                appen.
              </Alert>
            </div>
            <div className={ui.mf}>
              <Button onClick={() => setShowModal(false)}>Ferdig</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UsersPage;
